import { FormEvent, useState } from "react";
import { FormControl } from "@mui/material";
import { useAppSelector } from "@/store/hooks";
import { selectDialogData } from "@/store/ses/sesArchiveSlice";
import useTranslate from "@/hooks/useTranslate";
import { getStringWithoutLinesAndExtraSpacesAndButtonsFromArray } from "@/helpers";
import { LIST_OF_SCRIPT_AUTOTEST_PHRASES } from "@/constants/localStorageNames";
import ModalFormWindow from "@/HOC/ModalFormWindow/ModalFormWindow";
import CustomTextField from "@/components/MUI/CustomTextField/CustomTextField";
import CustomButton from "@/components/MUI/CustomButton/CustomButton";
import { IFormAddingScriptProps, IPhrasesItemLS } from "./FormAddingScript.props";

const FormAddingScript = ({ showModal, setShowModal, setShowNotification }: IFormAddingScriptProps): JSX.Element => {
	const [inputName, setInputName] = useState<string>(""); // название скрипта
	const [showErrorExistingName, setShowErrorExistingName] = useState<boolean>(false); // флаг существующего имени
	const [showErrorNoData, setShowErrorNoData] = useState<boolean>(false); // флаг отсутствия данных

	const dialogData = useAppSelector(selectDialogData); // store - данные диалога

	const translate = useTranslate(); // hook для перевода текста

	// обработчик закрытия формы
	const handleClose = (): void => {
		setShowModal(false);
	};

	// обработчик импорта
	const submitHandler = (e: FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		showErrorExistingName && setShowErrorExistingName(false); // убираем ошибку, если была
		showErrorNoData && setShowErrorNoData(false); // убираем ошибку, если была

		// если есть диалог
		if (Array.isArray(dialogData.data) && dialogData.data.length > 0) {
			// форматированные данные под скрипт тестирования
			const formattedDataForTestingScript: IPhrasesItemLS[] = dialogData.data
				.filter((messageData) => typeof messageData.request?.message === "string" && messageData.request.message !== "")
				.map((messageData) => ({
					question: typeof messageData.request?.message === "string" ? messageData.request.message : "unknown",
					answer: messageData.reply.answers?.map((answerItem) => getStringWithoutLinesAndExtraSpacesAndButtonsFromArray(answerItem.messages)).join(" ") || "",
					breakpoint: false,
					checktype: "full",
				}));

			// если нет данных - ошибка
			if (formattedDataForTestingScript.length === 0) {
				setShowErrorNoData(true);
				return;
			}
			const newData = {
				[inputName]: formattedDataForTestingScript,
			};

			const localStorageData = localStorage.getItem(LIST_OF_SCRIPT_AUTOTEST_PHRASES);
			// если есть данные из localStorage
			if (typeof localStorageData === "string") {
				try {
					const oldData: unknown = JSON.parse(localStorageData); // имеющиеся данные
					if (typeof oldData === "object" && oldData) {
						// если уже есть такое название - ошибка
						if (Object.keys(oldData).includes(inputName)) {
							setShowErrorExistingName(true);
							return;
						}
						localStorage.setItem(LIST_OF_SCRIPT_AUTOTEST_PHRASES, JSON.stringify({ ...oldData, ...newData })); // дописываем
					} else {
						localStorage.setItem(LIST_OF_SCRIPT_AUTOTEST_PHRASES, JSON.stringify(newData));
					}
				} catch {
					localStorage.setItem(LIST_OF_SCRIPT_AUTOTEST_PHRASES, JSON.stringify(newData));
				}
			} else {
				localStorage.setItem(LIST_OF_SCRIPT_AUTOTEST_PHRASES, JSON.stringify(newData));
			}
			setShowNotification(true); // включаем уведомление
		}

		handleClose(); // закрываем форму
	};

	return (
		<ModalFormWindow showModal={showModal} setShowModal={setShowModal} headerTitle={translate("title_addingScript")} close={handleClose}>
			<form onSubmit={submitHandler}>
				{/* название */}
				<FormControl fullWidth margin="dense">
					<CustomTextField
						autoFocus
						required
						label={translate("input_name")}
						value={inputName}
						onChange={(e) => setInputName(e.target.value)}
						error={showErrorExistingName || showErrorNoData}
						helperText={showErrorExistingName ? translate("title_scriptExists") : showErrorNoData ? translate("title_noData") : ""}
					/>
				</FormControl>

				<FormControl fullWidth margin="dense">
					<CustomButton type="submit">{translate("button_add")}</CustomButton>
				</FormControl>
			</form>
		</ModalFormWindow>
	);
};

export default FormAddingScript;
